/**
 * ICS Calendar File Generator
 * Generates RFC 5545 compliant iCalendar files for roster exports
 */

// Format date+time to ICS format: 20260220T180000
function formatICSDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const datePart = dateStr.replace(/-/g, '');
  if (!timeStr) return datePart + 'T000000';
  const [h, m] = timeStr.split(':');
  return `${datePart}T${h.padStart(2, '0')}${m.padStart(2, '0')}00`;
}

// Add minutes to a time string, return new time string
function addMinutesToTime(dateStr, timeStr, minutes) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const date = new Date(dateStr + 'T00:00:00');
  date.setHours(h, m + minutes, 0, 0);
  const newH = String(date.getHours()).padStart(2, '0');
  const newM = String(date.getMinutes()).padStart(2, '0');
  const newDateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  return `${newDateStr}T${newH}${newM}00`;
}

// Get current UTC timestamp in ICS format
function getUTCTimestamp() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const mo = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  const h = String(now.getUTCHours()).padStart(2, '0');
  const mi = String(now.getUTCMinutes()).padStart(2, '0');
  const s = String(now.getUTCSeconds()).padStart(2, '0');
  return `${y}${mo}${d}T${h}${mi}${s}Z`;
}

// Fold long lines per ICS spec (max 75 octets per line)
function foldLine(line) {
  if (line.length <= 75) return line;
  let result = line.substring(0, 75);
  let remaining = line.substring(75);
  while (remaining.length > 0) {
    result += '\r\n ' + remaining.substring(0, 74);
    remaining = remaining.substring(74);
  }
  return result;
}

// Escape special characters in ICS text values
function escapeText(text) {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generate an ICS calendar string from roster items
 * @param {Array} items - Array of objects with: type, date, startTime, endTime, summary, location, description, uid
 * @returns {string} ICS file content
 */
export function generateICSString(items) {
  const dtstamp = getUTCTimestamp();
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BOMS Referee Roster//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:My Roster'
  ];

  items.forEach(item => {
    const dtstart = formatICSDateTime(item.date, item.startTime);
    if (!dtstart) return;

    let dtend;
    if (item.endTime) {
      dtend = formatICSDateTime(item.date, item.endTime);
      // Handle case where end time is computed and may cross midnight
      if (!dtend) dtend = addMinutesToTime(item.date, item.startTime, 60);
    } else if (item.durationMinutes) {
      dtend = addMinutesToTime(item.date, item.startTime, item.durationMinutes);
    } else {
      // Default 1 hour duration
      dtend = addMinutesToTime(item.date, item.startTime, 60);
    }

    if (!dtend) dtend = dtstart;

    lines.push('BEGIN:VEVENT');
    lines.push(foldLine(`UID:${item.uid}`));
    lines.push(`DTSTAMP:${dtstamp}`);
    lines.push(`DTSTART:${dtstart}`);
    lines.push(`DTEND:${dtend}`);
    lines.push(foldLine(`SUMMARY:${escapeText(item.summary)}`));
    if (item.location) {
      lines.push(foldLine(`LOCATION:${escapeText(item.location)}`));
    }
    if (item.description) {
      lines.push(foldLine(`DESCRIPTION:${escapeText(item.description)}`));
    }
    lines.push('END:VEVENT');
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/**
 * Generate and download an ICS file
 * @param {Array} items - Array of roster items to export
 * @param {string} filename - Download filename (default: 'my-roster.ics')
 */
export function downloadICS(items, filename = 'my-roster.ics') {
  const icsContent = generateICSString(items);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
